/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect, useMemo, useRef, useState } from 'react';

import cloneDeep from 'lodash/cloneDeep';

import htmlToPlainText from 'bundles/assess-common/utils/htmlToPlainText';
import CMLEditor from 'bundles/authoring/content-authoring/components/CMLEditor';
import type { Props as CmlEditorProps } from 'bundles/authoring/content-authoring/components/CMLEditor';
import type { CmlContent as NaptimeCmlContent } from 'bundles/cml';
import * as gradingCmlUtils from 'bundles/grading-common/utils/cmlUtils';
import ItemList from 'bundles/grading/components/staff-graded-assignments/reusable-comments/ItemList';
import type {
  CmlContentInput,
  OwnCommentsEdge,
} from 'bundles/grading/components/staff-graded-assignments/reusable-comments/api/types';
import {
  commentToNaptimeCml,
  naptimeCmlToContentInput,
} from 'bundles/grading/components/staff-graded-assignments/reusable-comments/utils';
import { useDebouncedValue, useKeyDownHandler, useOutsideClickHandler } from 'bundles/grading/utils/hooks';

const styles = {
  container: css`
    position: relative;
    border-radius: var(--cds-border-radius-50) !important;

    .rc-CMLEditor {
      border-radius: var(--cds-border-radius-50) !important;

      [role='toolbar'] {
        border-top-left-radius: var(--cds-border-radius-50);
        border-top-right-radius: var(--cds-border-radius-50);
      }
    }
  `,
};

export type Props = {
  editorProps: CmlEditorProps;
  onReusableCommentChange: (cml: CmlContentInput) => void;
  onCommentReuse: (id: string) => void;
  getItemList: (filterCml?: CmlContentInput) => Promise<OwnCommentsEdge[] | undefined>;
  deleteItem: (id: string) => Promise<boolean>;
};

/**
 * Autosuggest / typeahead drop down enhanced CMLEditor
 */
function CmlEditorDropdown({ editorProps, onReusableCommentChange, onCommentReuse, getItemList, deleteItem }: Props) {
  const [suggestions, setSuggestions] = useState<OwnCommentsEdge[]>([]);
  const [filterCml, setFilterCml] = useState<NaptimeCmlContent | undefined>(editorProps?.initialCML);
  const debouncedFilterCml = useDebouncedValue(filterCml, 100);
  const [rerenderKey, setRerenderKey] = useState<number>(Date.now());
  const [focused, setFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // workaround to keep the list open during delete action
  const isDeletingRef = useRef<boolean>(false);

  // close dropdown on clicking outside
  useOutsideClickHandler(containerRef, () => {
    setFocused(false);
  });

  // close dropdown on `escape`
  useKeyDownHandler((event: KeyboardEvent) => {
    if (event.code === 'Escape') {
      setFocused(false);
    }
  });

  const handleOnFocus = async () => {
    setFocused(true);
  };

  const handleOnBlur = () => {
    if (!isDeletingRef.current) {
      // handle `blur` event in all cases except clicking "delete" on the list
      setFocused(false);
    }
  };

  const handleOnContentChange = async (cml: NaptimeCmlContent) => {
    setFilterCml(cml);
    editorProps.onContentChange(cml);
    if (cml.definition.value !== gradingCmlUtils.EMPTY_CML_VALUE) {
      onReusableCommentChange(naptimeCmlToContentInput(cml));
    }
  };

  useEffect(() => {
    async function fetch() {
      const res = await getItemList();
      setSuggestions(res || []);
    }

    fetch();
    /*
      We don't want to put `getItemList` in this array, if we do we will get a lot of excess re-renders.
      We could in theory fix this by useCallback-ing it up above; except that it ultimately comes from a render-prop where we can't use hooks.
      This means we need to do something wonky *somewhere* so... I am choosing to do it as close to the place it is needed as possible.
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleItemSelect = async (item: OwnCommentsEdge) => {
    editorProps.onContentChange(commentToNaptimeCml(item.node.commentContent));
    onCommentReuse(item.node.id);
    setFilterCml(commentToNaptimeCml(item.node.commentContent));
    setRerenderKey(Date.now());
    setFocused(false);
  };

  const handleItemDelete = async (item: OwnCommentsEdge) => {
    // Clicking on the suggestions list causes `blur` event, and hides the list. We want to keep the list open after deletion.
    // `isDeleting` ref allows to track this scenario and avoid resetting `suggestions` in `handleOnBlur`
    const prevSuggestions = cloneDeep(suggestions);
    isDeletingRef.current = true;
    setSuggestions((oldArray) => oldArray.filter((oldItem) => oldItem.node.id !== item.node.id));
    const res = await deleteItem(item.node.id);
    if (!res) {
      setSuggestions(prevSuggestions);
    }
    isDeletingRef.current = false;
  };

  const filteredSuggestions = useMemo(() => {
    const input = htmlToPlainText(debouncedFilterCml?.definition.value || '')
      .trim()
      .toLowerCase();

    if (!input) {
      return suggestions.slice(0, 5);
    }

    return suggestions
      .filter((itemCml) => {
        const item = htmlToPlainText(itemCml.node.commentContent.cmlValue).trim().toLowerCase();
        return item !== input && item.includes(input);
      })
      .slice(0, 5);
    // eslint complains here because we don't *use* focused for this function, but we do want to recalculate this when focused, so we disable it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestions, debouncedFilterCml, focused]);

  const emptyValue = !(
    editorProps.initialCML && editorProps.initialCML.definition.value !== gradingCmlUtils.EMPTY_CML_VALUE
  );

  // CMLEditor only accepts `initialCML` and it doesn't let setting a new value via props passed by a parent component.
  // We use `key` prop do a little hack with one ms delay for `isResetting` to unmount/remount the same component with a new value.
  return (
    <div css={styles.container} ref={containerRef} data-testid="cml-editor-dropdown">
      <CMLEditor
        {...editorProps}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onContentChange={handleOnContentChange}
        toolbarPosition="top"
        placeholder={filteredSuggestions.length > 0 ? 'Enter text or reuse a recent comment' : 'Enter text here'}
        key={rerenderKey}
      >
        {focused && filteredSuggestions.length > 0 && (
          <ItemList
            items={filteredSuggestions}
            allowDelete={true}
            onItemSelect={handleItemSelect}
            onItemDelete={handleItemDelete}
            showListHeading={emptyValue}
          />
        )}
      </CMLEditor>
    </div>
  );
}

export default CmlEditorDropdown;
